<template>
  <div 
    class="doc-window"
    :class="{ maximized: isMaximized }"
    :style="{
      display: isOpen ? 'block' : 'none',
      left: position.left + 'px',
      top: position.top + 'px',
      zIndex: zIndex,
      width: isMaximized ? (isMobile ? '100%' : '936px') : '400px',
      height: isMaximized ? (isMobile ? '100%' : '80%') : '300px',
      maxWidth: isMaximized && !isMobile ? '936px' : (isMobile ? '90%' : '400px')
    }"
    ref="docWindow"
    @mousedown="$emit('focus')"
  >
    <div 
      class="window-header"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <span class="window-title">{{ currentDocument.title }}</span>
      <div class="window-controls">
        <button 
          class="close" 
          @click.stop="handleClose"
          @touchend.prevent.stop="handleClose"
        ></button>
        <button 
          class="minimize" 
          @click.stop="handleMinimize"
          @touchend.prevent.stop="handleMinimize"
        ></button>
        <button 
          class="maximize" 
          @click.stop="handleMaximize"
          @touchend.prevent.stop="handleMaximize"
        ></button>
      </div>
    </div>
    <div class="window-content">
      <div v-html="currentDocument.content" class="prose prose-invert"></div>
    </div>
  </div>
</template>

<script>
import { pages } from '../data/pages';

export default {
  name: 'DocumentViewer',
  props: {
    slug: {
      type: String,
      required: true
    },
    initialPosition: {
      type: Object,
      default: () => ({ left: 120, top: 120 })
    },
    zIndex: {
      type: Number,
      default: 1
    },
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      position: { ...this.initialPosition },
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      isMaximized: false,
      isMobile: false
    };
  },
  computed: {
    currentDocument() {
      return pages[this.slug] || {
        title: 'Not Found',
        content: '<p>Document not found.</p>'
      };
    }
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$emit('focus'); // Bring to front when opened from taskbar
      }
    }
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 936;
    },
    startDrag(event) {
      if (this.isMaximized) return;

      this.isDragging = true;
      if (event.type === 'touchstart') {
        event.preventDefault();
        const touch = event.touches[0];
        this.dragOffset = {
          x: touch.clientX - this.position.left,
          y: touch.clientY - this.position.top
        };

        const touchMoveHandler = (e) => {
          e.preventDefault();
          if (this.isDragging) {
            const touch = e.touches[0];
            this.position = this.keepInBounds({
              left: touch.clientX - this.dragOffset.x,
              top: touch.clientY - this.dragOffset.y
            });
          }
        };

        const touchEndHandler = () => {
          this.isDragging = false;
          document.removeEventListener('touchmove', touchMoveHandler);
          document.removeEventListener('touchend', touchEndHandler);
        };

        document.addEventListener('touchmove', touchMoveHandler, { passive: false });
        document.addEventListener('touchend', touchEndHandler);
      } else {
        this.dragOffset = {
          x: event.clientX - this.position.left,
          y: event.clientY - this.position.top
        };

        const moveHandler = (e) => {
          if (this.isDragging) {
            this.position = this.keepInBounds({
              left: e.clientX - this.dragOffset.x,
              top: e.clientY - this.dragOffset.y
            });
          }
        };

        const upHandler = () => {
          this.isDragging = false;
          document.removeEventListener('mousemove', moveHandler);
          document.removeEventListener('mouseup', upHandler);
        };

        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', upHandler);
      }
    },
    keepInBounds(position) {
      if (!this.$refs.docWindow) return position;

      const docWidth = this.$refs.docWindow.offsetWidth;
      const docHeight = this.$refs.docWindow.offsetHeight;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Account for taskbar height
      const taskbarHeight = 48;
      const maxTop = windowHeight - docHeight - taskbarHeight;

      return {
        left: Math.max(0, Math.min(position.left, windowWidth - docWidth)),
        top: Math.max(0, Math.min(position.top, maxTop))
      };
    },
    handleClose(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.$emit('close');
    },
    handleMinimize(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.$emit('minimize');
    },
    handleMaximize(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.toggleMaximize();
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      if (this.isMaximized) {
        this.position = { 
          left: this.isMobile ? 0 : Math.max(0, (window.innerWidth - 936) / 2),
          top: this.isMobile ? 0 : 40
        };
      } else {
        // Keep window in bounds when un-maximizing
        this.position = this.keepInBounds({ left: 100, top: 100 });
      }
    }
  }
};
</script>

<style scoped>
.doc-window {
  display: none;
  position: absolute;
  width: 400px;
  max-width: 400px;
  height: 300px;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  resize: both;
  background: #ffffff;
  color: #000000;
}

.window-header {
  background: #f0f0f0;
  color: #000000;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
}

.window-title {
  font-size: 14px;
  font-weight: bold;
}

.window-controls {
  display: flex;
  gap: 8px;
}

.window-controls button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  min-width: 12px;
  min-height: 12px;
}

.window-controls .close {
  background: #f56565;
}

.window-controls .minimize {
  background: #f6e05e;
}

.window-controls .maximize {
  background: #48bb78;
}

.window-content {
  padding: 16px;
  height: calc(100% - 36px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 936px) {
  .doc-window {
    width: 90%;
    max-width: 90%;
    height: 40%;
    max-height: 40vh;
    left: 5% !important;
    top: 5% !important;
    resize: none;
    position: fixed;
  }

  .doc-window.maximized {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    left: 0 !important;
    top: 0 !important;
  }

  .window-controls {
    gap: 4px;
  }

  .window-controls button {
    width: 14px;
    height: 14px;
    min-width: 14px;
    min-height: 14px;
    margin: 0 2px;
  }
}
</style>
